import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Router, { useRouter, withRouter } from 'next/router';
import { inject, observer } from 'mobx-react';
import _get from 'lodash/get';
import MenuIcon from '@material-ui/icons/Menu';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { isLoggedIn } from '@utils/account';
import { useTranslation } from 'next-i18next';
import Paper from '@material-ui/core/Paper';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import cx from 'classnames';
import Link from 'next/link';
import Menu from '@material-ui/core/Menu';
import cookie from 'js-cookie';
import AdSense from 'react-ssr-adsense';
import useWindowSize from '@hooks/useWindowSize';
import StoreLogo from '@components/StoreLogo';
import { isShowAdSense } from '../../utils/store';

const styles = theme => ({
  topHeaderWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  userName: {
    color: '#fff',
    marginRight: 12,
    whiteSpace: 'nowrap',
    overflowX: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 80,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 'initial',
    },
  },
  topHeaderRoot: {
    backgroundColor: '#1C85E8',
    height: 50,
  },
  topHeaderBarWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  topHeaderBarContainer: {
    height: '100%',
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    alignSelf: 'self-start',
    [theme.breakpoints.up('sm')]: {
      flex: '1 0 40%',
      height: 46,
      alignItems: 'center',
      flexDirection: 'row',
      alignSelf: 'center',
    },
  },
  divider: {
    width: 2,
    backgroundColor: '#076BD1',
    margin: '0 25px',
    display: 'none',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  pageTitle: {
    fontSize: 20,
    color: '#076BD1',
    [theme.breakpoints.up('sm')]: {
      fontSize: 20,
    },
  },
  bottomHeaderBarContainer: {
    marginBottom: 13,
    marginTop: 13,
    display: 'flex',
    flexDirection: 'column-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'inherit',
      marginBottom: 29,
    },
  },
  logo: {
    marginBottom: 32,
    [theme.breakpoints.up('sm')]: {
      marginBottom: 0,
    },
  },
  menuIcon: {
    marginLeft: 10,
    cursor: 'pointer',
    color: '#fff',
    [theme.breakpoints.up('sm')]: {
      marginLeft: 30,
    },
  },
  menu: {
    position: 'relative',
    top: 10,
  },
  subMenuContainer: {
    position: 'relative',
  },
  subMenuWrapper: {
    display: 'none',
    position: 'absolute',
    right: 0,
    top: 40,
  },
  subMenuWrapperOpen: {
    display: 'block',
    zIndex: 9999,
  },
  subMenuIcon: {
    paddingRight: 5,
  },
  subMenuLabel: {
    color: '#58657C',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #E1E4EB',
    '&:last-child': {
      borderBottom: 0,
    },
  },
  myOrders: {
    color: '#fff',
    marginRight: 30,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  language: {
    color: '#fff',
    paddingRight: 15,
  },
  switchLanguageWrapper: {
    display: 'flex',
    paddingRight: 15,
    cursor: 'pointer',
  },
  switchLocale: {
    color: '#fff',
    paddingRight: 15,
    whiteSpace: 'nowrap',
  },
});

const GlobalNavigation = inject('store')(
  observer(
    ({
      classes,
      store: {
        accountStore,
        accountStore: { me = {} },
      },
      isShowLogo = true,
      facebookPageName,
      facebookPageId,
      pageTitle,
      subdomain,
      adsenseId = '',
    }) => {
      const [anchorEl, setAnchorEl] = React.useState(null);
      const router = useRouter();
      const { width } = useWindowSize();
      const { t } = useTranslation('common');
      const [open, setOpen] = React.useState(false);
      const [permissionUrl, setPermissionUrl] = React.useState('');
      const anchorRef = React.useRef(null);
      const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
      };
      const handleClickAway = event => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
          return;
        }
        setOpen(false);
      };
      const onClickLogout = () => {
        accountStore.logout();
        let redirect_url = '';
        if (typeof window !== undefined) {
          redirect_url = encodeURI(_get(window, 'location.href'));
        }
        Router.push(
          `${process.env.NEXT_PUBLIC_DOMAIN_URL}/permission?redirect_url=${redirect_url}`
        );
      };

      const handleClick = event => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const switchLocale = lang => {
        cookie.set('language', lang, {
          path: '/',
          domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        });
        window.location.href =
          lang === 'en' ? _get(router, 'asPath', '/') : `/${lang}${_get(router, 'asPath', '/')}`;
      };

      useEffect(() => {
        let permissionUrl = `${process.env.NEXT_PUBLIC_DOMAIN_URL}/permission?redirect_url=${_get(
          window,
          'location.href'
        )}`;
        if (subdomain) {
          permissionUrl = `${permissionUrl}&subdomain=${subdomain}`;
        }
        setPermissionUrl(permissionUrl);
      }, [subdomain]);

      return (
        <div>
          <div className={classes.topHeaderRoot}>
            <Grid
              container
              justify="flex-end"
              className={[classes.topHeaderBarContainer, 'container'].join(' ')}
            >
              <Grid item xs={12} className={classes.topHeaderBarWrapper}>
                <div className={classes.topHeaderWrapper}>
                  <div className={classes.switchLanguageWrapper}>
                    <span className={classes.switchLocale} onClick={handleClick}>
                      {t('change-language')}
                    </span>
                    <Menu
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                    >
                      <MenuItem onClick={switchLocale.bind(this, 'en')}>English</MenuItem>
                      <MenuItem onClick={switchLocale.bind(this, 'cn')} locale="cn">
                        中文
                      </MenuItem>
                    </Menu>
                  </div>
                  {isLoggedIn() ? (
                    <>
                      <Link href={`${process.env.NEXT_PUBLIC_DOMAIN_URL}/orders`}>
                        <a className={classes.myOrders}> {t('menu.my-orders')}</a>
                      </Link>
                      <Typography
                        variant="body1"
                        className={classes.userName}
                        onMouseOver={() => setOpen(true)}
                      >
                        {_get(me, 'name')}
                      </Typography>
                      <Avatar src="/static/images/avatarPlaceholder.svg" />
                    </>
                  ) : (
                    <Link href={permissionUrl}>
                      <Avatar src="/static/images/avatarPlaceholder.svg" />
                    </Link>
                  )}
                  {isLoggedIn() && (
                    <div className={classes.subMenuContainer}>
                      <MenuIcon
                        ref={anchorRef}
                        onClick={handleToggle}
                        aria-controls="account-menu"
                        className={classes.menuIcon}
                        onMouseOver={() => setOpen(true)}
                      />
                      <ClickAwayListener onClickAway={handleClickAway}>
                        <div
                          className={cx(classes.subMenuWrapper, {
                            [classes.subMenuWrapperOpen]: open === true,
                          })}
                        >
                          <Paper>
                            <MenuList>
                              <MenuItem onClick={onClickLogout} className={classes.subMenuLabel}>
                                <img
                                  src="/static/images/logoutIcon.svg"
                                  className={classes.subMenuIcon}
                                />
                                Log Out
                              </MenuItem>
                            </MenuList>
                          </Paper>
                        </div>
                      </ClickAwayListener>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
          {isShowLogo === true ? (
            <Grid container justify="flex-end" className="container">
              <Grid item xs={12} className={classes.bottomHeaderBarContainer}>
                <div className={classes.logoWrapper}>
                  {facebookPageName ? (
                    <StoreLogo
                      facebookPageId={facebookPageId}
                      facebookPageName={facebookPageName}
                    />
                  ) : (
                    <img src="/static/images/logo.svg" className={classes.logo} />
                  )}
                  {pageTitle && (
                    <>
                      <div className={classes.divider} />
                      <Typography className={classes.pageTitle}>{pageTitle}</Typography>
                    </>
                  )}
                </div>
                <div>
                  {isShowAdSense({
                    facebookPageId,
                  }) && adsenseId ? (
                    width > 768 ? (
                      <AdSense
                        client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                        slot={adsenseId}
                        style={{ display: 'block', width: 728, height: 90, margin: '0 auto' }}
                        format=""
                      />
                    ) : (
                      <>
                        <AdSense
                          client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                          slot="9607141480"
                          style={{ display: 'block', width: 320, height: 100, margin: '0 auto' }}
                          format=""
                        />
                        <br />
                      </>
                    )
                  ) : null}
                </div>
              </Grid>
            </Grid>
          ) : null}
        </div>
      );
    }
  )
);
GlobalNavigation.propTypes = {
  classes: PropTypes.object.isRequired,
  pageTitle: PropTypes.string,
  router: PropTypes.object,
  isShowLogo: PropTypes.bool,
  facebookPageName: PropTypes.string,
  facebookPageId: PropTypes.string,
  subdomain: PropTypes.string,
};

export default withRouter(withStyles(styles)(GlobalNavigation));
